import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ActivityIndicator,
  Linking,
  Modal,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { LoginOptionsPanel, PlatformIcon } from '@cubemeet/ui';
import { signInWithIdentifier, getCurrentUser } from '@cubemeet/core/store/slices/authSlice';
import { supabase } from '@cubemeet/core/services/supabase';
import { useI18n } from '../i18n';
import type { AppDispatch, RootState } from '../store';

const AuthShell: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // 登录成功优先跳 referer，没有则跳首页；返回按钮和退出登录强制跳首页
  const getReferer = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const refererQS = params.get('referer');
    if (refererQS) return refererQS;
    if (location.state && typeof location.state === 'object' && location.state.referer) {
      return location.state.referer;
    }
    return '/';
  }, [location]);
  const goHome = () => navigate('/', { replace: true });
  const { translations, isLoading } = useI18n();
  const dispatch = useDispatch<AppDispatch>();
  const authState = useSelector((state: RootState) => (state as any)?.auth ?? {}) as any;
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [helperMessage, setHelperMessage] = useState<string | null>(null);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [privacyModalVisible, setPrivacyModalVisible] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [identifierError, setIdentifierError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const authLoading: boolean = Boolean(authState?.loading);
  const authError: string | null = authState?.error ?? null;
  const isAuthenticated: boolean = Boolean(authState?.isAuthenticated);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const hasRedirectedRef = useRef(false);
  const lastRefererRef = useRef<string | null>(null);

  const t = useMemo(() => {
    return (key: string): string => {
      if (!translations) return key;
      const value = key.split('.').reduce<any>((acc, part) => {
        if (acc && typeof acc === 'object' && part in acc) {
          return acc[part];
        }
        return null;
      }, translations);

      return typeof value === 'string' ? value : key;
    };
  }, [translations]);

  const ensurePrivacy = useCallback(
    (action: () => void) => {
      if (!privacyAgreed) {
        setHelperMessage(t('auth.validation.termsRequired'));
        pendingActionRef.current = action;
        setPrivacyModalVisible(true);
        return;
      }
      setHelperMessage(null);
      action();
    },
    [privacyAgreed, t],
  );

  const validateIdentifier = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        setIdentifierError(t('auth.validation.identifierRequired'));
        return false;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(trimmed)) {
        setIdentifierError('');
        return true;
      }

      const usernameRegex = /^[a-zA-Z0-9_-]{3,20}$/;
      if (!usernameRegex.test(trimmed)) {
        setIdentifierError(t('auth.validation.identifierInvalid'));
        return false;
      }

      setIdentifierError('');
      return true;
    },
    [t],
  );

  const validatePassword = useCallback(
    (value: string) => {
      if (!value) {
        setPasswordError(t('auth.validation.passwordRequired'));
        return false;
      }

      if (value.length < 6) {
        setPasswordError(t('auth.validation.passwordLength'));
        return false;
      }

      setPasswordError('');
      return true;
    },
    [t],
  );

  const handlePasswordLogin = useCallback(() => {
    ensurePrivacy(() => {
      setPasswordModalVisible(true);
      setHelperMessage(null);
    });
  }, [ensurePrivacy]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasRedirectedRef.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const ref = getReferer();
    if (ref && ref !== '/auth' && ref !== '/auth/' && ref.trim() !== '') {
      lastRefererRef.current = ref;
    } else if (location.pathname === '/auth') {
      lastRefererRef.current = null;
    }
  }, [getReferer, location.pathname]);

  const getPostLoginRoute = useCallback(() => {
    if (typeof window === 'undefined') return 'dashboard';

    const KNOWN_MODULES = ['travel', 'carpool', 'ledger', 'chat', 'todo', 'profile', 'notifications', 'friends'];

    const hostname = window.location.hostname || '';

    // Explicit query param override
    const urlParams = new URLSearchParams(window.location.search || '');
    const paramModule = urlParams.get('module');
    if (paramModule && KNOWN_MODULES.includes(paramModule)) return paramModule;

    // Hostname detection (e.g., travel.cubemeet.app)
    const h = hostname.toLowerCase();
    for (const mod of KNOWN_MODULES) {
      if (h === `${mod}.cubemeet.app` || h.startsWith(`${mod}.`)) return mod;
    }

    return 'dashboard';
  }, []);

  useEffect(() => {
    if (!isAuthenticated || hasRedirectedRef.current) return;

  const referer = lastRefererRef.current;
    let target: string | null = null;
    if (referer && referer !== '/auth' && referer !== '/auth/' && referer.trim() !== '') {
      target = referer;
    }

    let storedRedirect: { path?: string; search?: string; hash?: string } | null = null;
    if (typeof window !== 'undefined') {
      try {
        const raw = window.sessionStorage.getItem('cubemeet:postAuthRedirect');
        if (raw) {
          try {
            storedRedirect = JSON.parse(raw);
          } catch (err) {
            console.warn('Failed to parse stored post-auth redirect hint', err);
          }
        }
      } catch (err) {
        console.warn('Unable to read post-auth redirect hint', err);
      }

      try {
        window.sessionStorage.removeItem('cubemeet:postAuthRedirect');
      } catch (err) {
        console.warn('Unable to clear post-auth redirect hint', err);
      }
    }

    if (!target && storedRedirect) {
      const path = storedRedirect.path || '/';
      const search = storedRedirect.search || '';
      const hash = storedRedirect.hash ? (storedRedirect.hash.startsWith('#') ? storedRedirect.hash : `#${storedRedirect.hash}`) : '';
      target = `${path}${search}${hash}`;
    }

    if (!target) {
      const fallbackModule = getPostLoginRoute();
      if (fallbackModule === 'dashboard') {
        target = '/';
      } else {
        target = `/${fallbackModule}#${fallbackModule}`;
      }
    }

    if (!target) return;

    if (target.startsWith('http')) {
      window.location.replace(target);
      hasRedirectedRef.current = true;
      return;
    }

    // Avoid redundant navigation
    const current = `${location.pathname}${location.search}${location.hash}`;
    if (target === current) {
      hasRedirectedRef.current = true;
      return;
    }

    hasRedirectedRef.current = true;
    navigate(target, { replace: true });
  }, [isAuthenticated, navigate, getPostLoginRoute]);

  const handleGoogleLogin = useCallback(() => {
    ensurePrivacy(async () => {
      try {
        setHelperMessage(null);
  const targetRoute = getPostLoginRoute();
  const search = window.location.search || '';
  const redirectTo = `${window.location.origin}${window.location.pathname}${search}#${targetRoute}`;

        // Request offline access to obtain refresh token when possible
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo,
            queryParams: {
              access_type: 'offline',
              prompt: 'consent',
            },
          },
        });

        if (error) {
          setHelperMessage(error.message || t('auth.loginFailed'));
          return;
        }

        // supabase may either perform a browser redirect for us or return a data.url
        // When data.url is provided (skipBrowserRedirect flow), navigate there explicitly
        if (data && (data as any).url) {
          try {
            window.location.href = (data as any).url;
          } catch (navErr) {
            // Fallback: open in a new window
            window.open((data as any).url, '_blank', 'noopener');
          }
        }
      } catch (err: any) {
        setHelperMessage(err?.message || t('auth.loginFailed'));
      }
    });
  }, [ensurePrivacy, t]);
  const handleRegister = useCallback(() => {
    ensurePrivacy(() => {
      // Navigate to local web register page for consistent UX
      navigate('/auth/register');
    });
  }, [ensurePrivacy, navigate]);

  const handleSubmitLogin = useCallback(async () => {
    const identifierValid = validateIdentifier(identifier);
    const passwordValid = validatePassword(password);
    if (!identifierValid || !passwordValid) return;

    try {
      await dispatch(
        signInWithIdentifier({ identifier: identifier.trim(), password })
      ).unwrap();
      await dispatch(getCurrentUser()).unwrap();
      setPasswordModalVisible(false);
      setHelperMessage(null);
      setPasswordError('');
      setIdentifierError('');
      // 登录成功优先跳 referer，没有则跳首页
      const referer = getReferer();
      navigate(referer, { replace: true });
    } catch (error: any) {
      let message = String(error || '');
      if (message.includes('Invalid login credentials')) {
        message = t('auth.loginFailed');
      } else if (message.includes('Username') && message.includes('does not exist')) {
        message = t('auth.validation.usernameNotExists');
      } else if (message.includes('Database query error')) {
        message = t('errors.serverError');
      }
      setPasswordError(message);
    }
  }, [dispatch, getCurrentUser, identifier, password, t, validateIdentifier, validatePassword, navigate, getReferer]);

  const closePasswordModal = useCallback(() => {
    setPasswordModalVisible(false);
  }, []);

  const openLink = useCallback((url: string) => {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener');
    } else {
      void Linking.openURL(url);
    }
  }, []);

  const renderAuthError = () => {
    const message = passwordError || helperMessage || authError;
    if (!message) return null;
    return <Text style={styles.errorMessage}>{message}</Text>;
  };

  // Determine if user has navigation history (can go back)
  const canGoBack = typeof window !== 'undefined' && window.history.length > 1;

  const handleBack = useCallback(() => {
    // If we can go back in browser history, prefer that
    if (canGoBack) {
      window.history.back();
    } else {
      // Fallback: navigate to referer or home
      navigate(getReferer(), { replace: true });
    }
  }, [canGoBack, navigate, getReferer]);

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* Back/Close Button */}
      <View style={{ position: 'absolute', top: 24, left: 20, zIndex: 10 }}>
        <TouchableOpacity
          onPress={handleBack}
          style={{ flexDirection: 'row', alignItems: 'center', padding: 4 }}
          accessibilityRole="button"
          accessibilityLabel={canGoBack ? (t('common.back') || 'Back') : (t('common.close') || 'Close')}
        >
          {canGoBack ? (
            <>
              <PlatformIcon name="arrow-back" size={24} color="#2563EB" />
              <Text style={{ color: '#2563EB', fontSize: 16, marginLeft: 4 }}>{t('common.back') || '返回'}</Text>
            </>
          ) : (
            <>
              <PlatformIcon name="close" size={24} color="#2563EB" />
              <Text style={{ color: '#2563EB', fontSize: 16, marginLeft: 4 }}>{t('common.close') || '关闭'}</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content} bounces={false}>
        <LoginOptionsPanel
          t={t}
          privacyAgreed={privacyAgreed}
          onTogglePrivacy={() => {
            setPrivacyAgreed((prev) => {
              const next = !prev;
              if (next) setHelperMessage(null);
              return next;
            });
          }}
          containerStyle={styles.panel}
          onPasswordLogin={handlePasswordLogin}
          onGoogleLogin={handleGoogleLogin}
          onRegister={handleRegister}
          onNavigateToTerms={() => openLink('/terms-of-service')}
          onNavigateToPrivacy={() => openLink('/privacy-policy')}
        />
        {renderAuthError()}
        {isLoading || authLoading ? (
          <Text style={styles.loading}>{t('common.loading')}</Text>
        ) : null}
      </ScrollView>

      <Modal
        visible={passwordModalVisible}
        animationType="slide"
        onRequestClose={closePasswordModal}
        transparent
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>{t('auth.passwordLogin')}</Text>
            <View style={styles.modalFormGroup}>
              <Text style={styles.modalLabel}>{t('auth.emailOrUsername')}</Text>
              <TextInput
                style={styles.modalInput}
                placeholder={t('auth.emailPlaceholder')}
                placeholderTextColor="#9CA3AF"
                autoCapitalize="none"
                autoCorrect={false}
                value={identifier}
                onChangeText={(value) => {
                  setIdentifier(value);
                  if (identifierError) validateIdentifier(value);
                }}
                onBlur={() => validateIdentifier(identifier)}
              />
              {identifierError ? (
                <Text style={styles.fieldError}>{identifierError}</Text>
              ) : null}
            </View>

            <View style={styles.modalFormGroup}>
              <Text style={styles.modalLabel}>{t('auth.password')}</Text>
              <View style={styles.passwordField}>
                <TextInput
                  style={[styles.modalInput, styles.inputWithIconRight]}
                  placeholder={t('auth.passwordPlaceholder')}
                  placeholderTextColor="#9CA3AF"
                  value={password}
                  onChangeText={(value) => {
                    setPassword(value);
                    if (passwordError) validatePassword(value);
                  }}
                  secureTextEntry={!showPassword}
                  onBlur={() => validatePassword(password)}
                />
                <TouchableOpacity
                  onPress={() => setShowPassword((prev) => !prev)}
                  style={styles.passwordIconButton}
                  accessibilityRole="button"
                  accessibilityLabel={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  <PlatformIcon name={showPassword ? 'visibility-off' : 'visibility'} size={22} color="#6B7280" />
                </TouchableOpacity>
              </View>
              {passwordError ? <Text style={styles.fieldError}>{passwordError}</Text> : null}
            </View>

            {/* Forgot password link (right-bottom under the password field) */}
            <View style={styles.forgotRow}>
              <TouchableOpacity onPress={() => { setPasswordModalVisible(false); navigate('/auth/forgot'); }}>
                <Text style={styles.forgotText}>{t('auth.forgotPassword') || '忘记密码？'}</Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={[styles.primaryButton, authLoading && styles.primaryButtonDisabled]}
              onPress={handleSubmitLogin}
              disabled={authLoading}
            >
              {authLoading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.primaryButtonText}>{t('auth.login')}</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity style={styles.secondaryLink} onPress={closePasswordModal}>
              <Text style={styles.secondaryLinkText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={privacyModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setPrivacyModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.privacyModal}>
            <Text style={styles.modalTitle}>{t('auth.termsAndPrivacyPolicy')}</Text>
            <Text style={styles.privacyText}>
              {t('auth.iHaveReadAndAgree')}
              <Text style={styles.privacyLink} onPress={() => openLink('/terms-of-service')}>
                {' '}{t('auth.termsOfService')}
              </Text>
              <Text> {t('auth.and')} </Text>
              <Text style={styles.privacyLink} onPress={() => openLink('/privacy-policy')}>
                {t('auth.privacyPolicy')}
              </Text>
            </Text>

            <View style={styles.privacyButtonsRow}>
              <TouchableOpacity
                style={[styles.privacyButton, styles.privacyCancelButton]}
                onPress={() => {
                  setPrivacyModalVisible(false);
                  pendingActionRef.current = null;
                }}
              >
                <Text style={styles.privacyCancelText}>{t('common.cancel')}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.privacyButton, styles.privacyAgreeButton]}
                onPress={() => {
                  const cb = pendingActionRef.current;
                  pendingActionRef.current = null;
                  setPrivacyAgreed(true);
                  setPrivacyModalVisible(false);
                  setHelperMessage(null);
                  if (typeof cb === 'function') cb();
                }}
              >
                <Text style={styles.privacyAgreeText}>{t('common.agree')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  scroll: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    justifyContent: 'space-between',
    alignItems: 'stretch',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 16,
    minHeight: '100%',
  },
  panel: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 540,
  },
  helper: {
    marginTop: -12,
    textAlign: 'center',
    color: '#EF4444',
    fontSize: 13,
  },
  loading: {
    marginTop: 16,
    textAlign: 'center',
    color: '#6B7280',
    fontSize: 12,
  },
  errorMessage: {
    marginTop: 12,
    textAlign: 'center',
    color: '#B91C1C',
    fontSize: 13,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 24,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
    textAlign: 'center',
    marginBottom: 16,
  },
  modalFormGroup: {
    marginBottom: 18,
  },
  modalLabel: {
    fontSize: 14,
    color: '#4B5563',
    marginBottom: 8,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    backgroundColor: '#F9FAFB',
    color: '#111827',
    // Ensure inputs take full width of form group
    width: '100%',
  },
  fieldError: {
    marginTop: 6,
    color: '#DC2626',
    fontSize: 12,
  },
  // Password field wraps input to place icon inside like the mobile app
  passwordField: {
    position: 'relative',
  },
  inputWithIconRight: {
    paddingRight: 42, // leave room for the eye icon
  },
  passwordIconButton: {
    position: 'absolute',
    right: 12,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  primaryButton: {
    marginTop: 8,
    backgroundColor: '#2563EB',
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonDisabled: {
    opacity: 0.7,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  secondaryLink: {
    marginTop: 16,
    alignItems: 'center',
  },
  secondaryLinkText: {
    color: '#6B7280',
    fontSize: 14,
  },
  privacyModal: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 24,
    alignItems: 'center',
  },
  privacyText: {
    fontSize: 14,
    color: '#374151',
    textAlign: 'center',
    marginBottom: 12,
  },
  privacyLink: {
    color: '#2563EB',
    fontWeight: '600',
  },
  privacyButtonsRow: {
    flexDirection: 'row',
    width: '100%',
    marginTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  privacyButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  privacyCancelButton: {
    borderRightWidth: 1,
    borderRightColor: '#E5E7EB',
  },
  privacyCancelText: {
    color: '#6B7280',
    fontSize: 14,
  },
  privacyAgreeButton: {},
  privacyAgreeText: {
    color: '#2563EB',
    fontSize: 14,
    fontWeight: '700',
  },
  // Forgot password row
  forgotRow: {
    alignItems: 'flex-end',
    marginTop: 8,
    marginBottom: 4,
  },
  forgotText: {
    color: '#2563EB',
    fontSize: 13,
  },
});

export default AuthShell;
